import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { CodeXml, Copy, Check, ArrowLeft, ArrowRight, Sparkles } from "lucide-react";
import axios from "axios";
import { getAuthHeaders } from "../services/authHeaders";
import { useNavigate } from "react-router-dom";

const PROXY_BASE_URL = "https://api.sobevenda.com.br/api-proxy";

// Classes padrão reutilizáveis
const inputCls = "w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400";
const labelCls = "block text-xs mb-1 text-slate-600 dark:text-slate-300 font-medium";

const STEP_LABELS = [
  "Tipo de funil",
  "Informações",
  "Conversão",
  "Conta Google",
  "Pixel",
  "Resumo",
  "Script",
];

// ─── Stepper ──────────────────────────────────────────────────────────────────

function StepIndicator({ step, total, labels }) {
  return (
    <>
      {/* Mobile: só mostra step atual */}
      <div className="flex items-center gap-3 mb-6 lg:hidden">
        <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold bg-slate-900 text-white dark:bg-white dark:text-slate-900 shrink-0">
          {step}
        </div>
        <div className="flex-1">
          <div className="text-sm font-bold text-slate-900 dark:text-slate-100">{labels[step - 1]}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">Etapa {step} de {total}</div>
        </div>
        {/* Barra de progresso */}
        <div className="w-24 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
          <div
            className="h-full rounded-full bg-slate-900 dark:bg-white transition-all duration-300"
            style={{ width: `${(step / total) * 100}%` }}
          />
        </div>
      </div>

      {/* Desktop: stepper completo */}
      <div className="hidden lg:flex items-start gap-0 mb-8">
        {labels.map((label, i) => (
          <div key={label} className="flex items-start flex-1">
            <div className="flex flex-col items-center flex-1">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 shrink-0 transition ${
                step > i + 1
                  ? 'bg-slate-900 border-slate-900 text-white dark:bg-white dark:border-white dark:text-slate-900'
                  : step === i + 1
                  ? 'bg-slate-900 border-slate-900 text-white dark:bg-white dark:border-white dark:text-slate-900'
                  : 'border-slate-300 dark:border-slate-600 text-slate-400 bg-white dark:bg-slate-900'
              }`}>
                {step > i + 1 ? <Check className="w-3.5 h-3.5" /> : i + 1}
              </div>
              <span className={`text-[10px] mt-1 text-center leading-tight px-1 ${
                step === i + 1
                  ? 'font-bold text-slate-900 dark:text-slate-100'
                  : step > i + 1
                  ? 'text-slate-600 dark:text-slate-400'
                  : 'text-slate-400 dark:text-slate-500'
              }`}>
                {label}
              </span>
            </div>
            {i < total - 1 && (
              <div className={`h-px flex-1 mt-3.5 transition ${
                step > i + 1 ? 'bg-slate-900 dark:bg-white' : 'bg-slate-200 dark:bg-slate-700'
              }`} />
            )}
          </div>
        ))}
      </div>
    </>
  );
}

// ─── Botão de opção (selecionável) ────────────────────────────────────────────

function OptionButton({ selected, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left p-4 rounded-lg border transition ${
        selected
          ? 'border-slate-900 bg-slate-50 dark:border-slate-100 dark:bg-slate-800'
          : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800/50'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center transition ${
          selected
            ? 'border-slate-900 dark:border-slate-100'
            : 'border-slate-300 dark:border-slate-600'
        }`}>
          {selected && <div className="w-2 h-2 rounded-full bg-slate-900 dark:bg-slate-100" />}
        </div>
        <div className="flex-1 min-w-0">{children}</div>
      </div>
    </button>
  );
}

// ─── ScriptCard ───────────────────────────────────────────────────────────────

function ScriptCard({ title, subtitle, script, onCopy }) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">{title}</h3>
          {subtitle && <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{subtitle}</p>}
        </div>
        <button
          type="button"
          onClick={onCopy}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-xs shrink-0"
        >
          <Copy className="w-3.5 h-3.5" /> Copiar
        </button>
      </div>
      <pre className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-3 text-xs font-mono text-slate-800 dark:text-slate-100 overflow-x-auto whitespace-pre-wrap break-all">
        {script || "// Script ainda não gerado"}
      </pre>
    </div>
  );
}

// ─── Botões de navegação ──────────────────────────────────────────────────────

function NavButtons({ onBack, onNext, nextLabel = "Próximo", nextDisabled = false, loading = false }) {
  return (
    <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100 dark:border-slate-800">
      {onBack ? (
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>
      ) : <div />}
      <button
        onClick={onNext}
        disabled={nextDisabled || loading}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-60 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
      >
        {loading ? "Gerando..." : nextLabel}
        {!loading && <ArrowRight className="w-4 h-4" />}
      </button>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

function SobeScript() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    funnel_mode: "single",
    platform: "",
    project_name: "",
    conversion_name: "",
    max_clicks: "",
    redirect: "no",
    redirect_url: "",
    auto_conversion: "no",
    selected_google_account_id: "",
    selected_pixel_level: "",
    selected_mcc_id: "",
    selected_submcc_id: "",
    selected_pixel_id: "",
    affiliate_link: "",
    producer_link: "",
    redirect_type: "always_affiliate",
  });

  const [step, setStep] = useState(1);
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [availableMCCs, setAvailableMCCs] = useState([]);
  const [availableSubMCCs, setAvailableSubMCCs] = useState([]);
  const [availableAccountsNivel, setAvailableAccountsNivel] = useState([]);
  const [availablePixels, setAvailablePixels] = useState([]);
  const [loadingMCCs, setLoadingMCCs] = useState(false);
  const [loadingSubMCCs, setLoadingSubMCCs] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingPixels, setLoadingPixels] = useState(false);
  const [generatedLink, setGeneratedLink] = useState("");
  const [accountsOfMCC, setAccountsOfMCC] = useState([]);
  const [loadingChildAccounts, setLoadingChildAccounts] = useState(false);
  const [platforms, setPlatforms] = useState([]);
  const [fullScriptText, setFullScriptText] = useState("");
  const [bridgeScriptText, setBridgeScriptText] = useState("");
  const [vslScriptText, setVslScriptText] = useState("");
  const [generating, setGenerating] = useState(false);

  // ── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    axios.get(`${PROXY_BASE_URL}/affiliate/platforms`, { headers: getAuthHeaders() })
      .then((res) => setPlatforms(res.data))
      .catch(() => setPlatforms([]));
  }, []);

  useEffect(() => {
    if (formData.auto_conversion === "yes") {
      axios.get(`${PROXY_BASE_URL}/affiliate/google-ads-accounts`, { headers: getAuthHeaders() })
        .then((res) => {
          const data = res.data;
          setAvailableAccounts(Array.isArray(data) ? data : data.accounts || []);
        })
        .catch(() => setAvailableAccounts([]));
    }
  }, [formData.auto_conversion]);

  useEffect(() => {
    if (formData.auto_conversion === "yes" && formData.selected_google_account_id &&
        (formData.selected_pixel_level === "mcc" || formData.selected_pixel_level === "submcc")) {
      setLoadingMCCs(true);
      axios.get(`${PROXY_BASE_URL}/affiliate/google-ads/available-accounts?google_ads_account_id=${formData.selected_google_account_id}&level=mcc`, { headers: getAuthHeaders() })
        .then((res) => {
          let list = Array.isArray(res.data) ? res.data : res.data.accounts || [];
          setAvailableMCCs(list.filter((acc) => acc.customer?.manager === true && acc.customer?.status === "ENABLED"));
        })
        .catch(() => setAvailableMCCs([]))
        .finally(() => setLoadingMCCs(false));
    } else {
      setAvailableMCCs([]);
    }
  }, [formData.selected_google_account_id, formData.selected_pixel_level, formData.auto_conversion]);

  useEffect(() => {
    if (formData.selected_pixel_level === "mcc" && formData.selected_mcc_id && formData.selected_google_account_id) {
      setLoadingPixels(true);
      axios.get(`${PROXY_BASE_URL}/affiliate/google-ads/pixels?mcc_id=${formData.selected_mcc_id}&id=${formData.selected_google_account_id}&login_customer_id=${formData.selected_mcc_id}`, { headers: getAuthHeaders() })
        .then((res) => setAvailablePixels(Array.isArray(res.data) ? res.data : []))
        .catch(() => setAvailablePixels([]))
        .finally(() => setLoadingPixels(false));
    } else if (formData.selected_pixel_level === "mcc") {
      setAvailablePixels([]);
    }
  }, [formData.selected_mcc_id, formData.selected_pixel_level, formData.selected_google_account_id]);

  useEffect(() => {
    if (formData.selected_pixel_level === "submcc" && formData.selected_mcc_id && formData.selected_google_account_id) {
      setLoadingSubMCCs(true);
      axios.get(`${PROXY_BASE_URL}/affiliate/google-ads/available-accounts?google_ads_account_id=${formData.selected_google_account_id}&level=submcc&parent_customer_id=${formData.selected_mcc_id}`, { headers: getAuthHeaders() })
        .then((res) => {
          let list = Array.isArray(res.data) ? res.data : res.data.accounts || [];
          setAvailableSubMCCs(list.filter((acc) => acc.customerClient?.manager === true && (acc.customerClient?.level === 1 || acc.customerClient?.level === "1") && acc.customerClient?.status === "ENABLED"));
        })
        .catch(() => setAvailableSubMCCs([]))
        .finally(() => setLoadingSubMCCs(false));
    } else if (formData.selected_pixel_level === "submcc") {
      setAvailableSubMCCs([]);
    }
  }, [formData.selected_mcc_id, formData.selected_pixel_level, formData.selected_google_account_id]);

  useEffect(() => {
    if (formData.selected_pixel_level === "submcc" && formData.selected_submcc_id && formData.selected_google_account_id && formData.selected_mcc_id) {
      setLoadingPixels(true);
      axios.get(`${PROXY_BASE_URL}/affiliate/google-ads/pixels?mcc_id=${formData.selected_submcc_id.replace("customers/", "")}&id=${formData.selected_google_account_id}&login_customer_id=${formData.selected_mcc_id}`, { headers: getAuthHeaders() })
        .then((res) => setAvailablePixels(Array.isArray(res.data) ? res.data : []))
        .catch(() => setAvailablePixels([]))
        .finally(() => setLoadingPixels(false));
    } else if (formData.selected_pixel_level === "submcc") {
      setAvailablePixels([]);
    }
  }, [formData.selected_submcc_id, formData.selected_pixel_level, formData.selected_google_account_id, formData.selected_mcc_id]);

  useEffect(() => {
    if (formData.selected_pixel_level === "account" && formData.selected_google_account_id) {
      setLoadingAccounts(true);
      axios.get(`${PROXY_BASE_URL}/affiliate/google-ads/available-accounts?google_ads_account_id=${formData.selected_google_account_id}&level=account`, { headers: getAuthHeaders() })
        .then((res) => {
          let list = Array.isArray(res.data) ? res.data : res.data.accounts || [];
          setAvailableAccountsNivel(list.filter((acc) => acc.customer?.status === "ENABLED"));
        })
        .catch(() => setAvailableAccountsNivel([]))
        .finally(() => setLoadingAccounts(false));
    } else if (formData.selected_pixel_level === "account") {
      setAvailableAccountsNivel([]);
    }
  }, [formData.selected_pixel_level, formData.selected_google_account_id]);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, selected_submcc_id: "", selected_pixel_id: "" }));
    setAvailableSubMCCs([]); setAvailableAccountsNivel([]); setAvailablePixels([]);
  }, [formData.selected_mcc_id]); // eslint-disable-line

  useEffect(() => {
    setFormData((prev) => ({ ...prev, selected_pixel_id: "" }));
    setAvailablePixels([]);
  }, [formData.selected_submcc_id]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const nextStep = () => setStep((prev) => prev + 1);
  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

  const handleCopyToClipboard = async (text) => {
    if (!text) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
      }
      toast.success("Script copiado para a área de transferência!");
    } catch { toast.error("Não foi possível copiar o script."); }
  };

  const handleGenerateScript = async () => {
    setGenerating(true);
    let payload = { ...formData, is_funnel: formData.funnel_mode === "bridge_vsl" ? 1 : 0 };

    if (formData.auto_conversion === "yes") {
      let selectedGoogleAccount = null;
      if (formData.selected_pixel_level === "account") {
        selectedGoogleAccount = availableAccountsNivel.find((acc) => acc.customer?.id === formData.selected_mcc_id) || availableAccountsNivel.find((acc) => acc.customer?.id === formData.selected_submcc_id);
      } else if (formData.selected_pixel_level === "mcc") {
        selectedGoogleAccount = availableMCCs.find((acc) => acc.customer?.id === formData.selected_mcc_id);
      }
      const selectedPixel = availablePixels.find((p) => p.id === formData.selected_pixel_id);
      payload = {
        ...payload,
        conversion_name: selectedPixel ? selectedPixel.conversion_name || selectedPixel.name || formData.conversion_name : formData.conversion_name,
        selected_pixel_id: selectedPixel ? selectedPixel.id : formData.selected_pixel_id,
        google_account_name: selectedGoogleAccount?.customer?.descriptiveName || "",
        google_account_currency: selectedGoogleAccount?.customer?.currencyCode || "",
        google_account_timezone: selectedGoogleAccount?.customer?.timeZone || "",
      };
    }

    try {
      const response = await axios.post(`${PROXY_BASE_URL}/script/create-sobescript`, payload, { headers: getAuthHeaders() });
      const { script_url } = response.data;
      if (!script_url) throw new Error("Resposta incompleta do backend: script_url ausente.");

      if (formData.funnel_mode === "bridge_vsl") {
        const bridgeTag = `<script defer src="${script_url}?funnel=bridge_vsl&stage=bridge_page"></script>`;
        const vslTag    = `<script defer src="${script_url}?funnel=bridge_vsl&stage=vsl_page"></script>`;
        const finalText = `<!-- Bridge Page (primeira página do funil) -->\n${bridgeTag}\n\n<!-- VSL (página da carta de vendas) -->\n${vslTag}`;
        setBridgeScriptText(bridgeTag); setVslScriptText(vslTag); setFullScriptText(finalText);
      } else {
        const singleTag = `<script defer src="${script_url}"></script>`;
        setBridgeScriptText(""); setVslScriptText(""); setFullScriptText(singleTag);
      }
      setGeneratedLink(fullScriptText);
      toast.success("Script gerado com sucesso!");
      setStep(7);
    } catch {
      toast.error("Erro ao gerar o script.");
    } finally {
      setGenerating(false);
    }
  };

  const subMCCs = availableSubMCCs.filter((acc) =>
    acc.customerClient && (acc.customerClient.level === 1 || acc.customerClient.level === "1") &&
    (acc.customerClient.manager === true || acc.customerClient.manager === "true") &&
    acc.customerClient.status === "ENABLED"
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="p-4 md:p-6">
      <div className="space-y-6">

        {/* HEADER */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <CodeXml className="w-6 h-6" />
            Gerar SobeScript
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
            Configure seu script de rastreamento passo a passo.
          </p>
        </div>

        {/* STEPPER + CONTEÚDO DO STEP */}
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 md:p-6">
          <StepIndicator step={step} total={STEP_LABELS.length} labels={STEP_LABELS} />

          {/* ── Step 1: Tipo de funil ── */}
          {step === 1 && (
            <div className="space-y-4">
              <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">
                Como vai ser o funil desse SobeScript?
              </h2>
              <div className="space-y-3">
                <OptionButton
                  selected={formData.funnel_mode === "single"}
                  onClick={() => setFormData((prev) => ({ ...prev, funnel_mode: "single" }))}
                >
                  <div className="text-sm font-bold text-slate-900 dark:text-slate-100">Presell + Página do produtor</div>
                  <div className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                    Script instalado na sua presell, levando direto para a página do produtor com camuflagem inteligente anti-bot (modelo padrão).
                  </div>
                </OptionButton>
                <OptionButton
                  selected={formData.funnel_mode === "bridge_vsl"}
                  onClick={() => setFormData((prev) => ({ ...prev, funnel_mode: "bridge_vsl" }))}
                >
                  <div className="text-sm font-bold text-slate-900 dark:text-slate-100">Bridge Page + VSL + Produtor</div>
                  <div className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                    Um único SobeScript para funcionar em toda a jornada: bridge page, página da VSL e página do produtor, com análise por sessão de funil.
                  </div>
                </OptionButton>
              </div>
              <NavButtons onNext={nextStep} nextLabel="Continuar" />
            </div>
          )}

          {/* ── Step 2: Informações ── */}
          {step === 2 && (
            <div className="space-y-4">
              <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">Informações do Script</h2>

              <div>
                <label className={labelCls}>Plataforma <span className="text-red-500">*</span></label>
                <select name="platform" value={formData.platform} onChange={handleChange} className={inputCls}>
                  <option value="">Selecione a plataforma</option>
                  {platforms.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
                </select>
              </div>

              <div>
                <label className={labelCls}>Nome do projeto <span className="text-red-500">*</span></label>
                <input type="text" name="project_name" value={formData.project_name} onChange={handleChange} className={inputCls} placeholder="Ex: Weight Loss" />
              </div>

              <div>
                <label className={labelCls}>Nome da ação de conversão <span className="text-red-500">*</span></label>
                <input type="text" name="conversion_name" value={formData.conversion_name} onChange={handleChange} className={inputCls} placeholder="Ex: pixel weight loss" />
              </div>

              {/* Smart toggle */}
              <div className="flex items-center gap-3 py-2">
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Smart SobeScript</span>
                <button
                  type="button"
                  aria-pressed={formData.redirect_type === "smart"}
                  onClick={() => setFormData((prev) => ({
                    ...prev,
                    redirect_type: prev.redirect_type === "smart" ? "always_affiliate" : "smart",
                    producer_link: prev.redirect_type === "smart" ? "" : prev.producer_link,
                    affiliate_link: prev.redirect_type === "smart" ? "" : prev.affiliate_link,
                  }))}
                  className={`relative inline-flex h-6 w-11 rounded-full transition-colors ${formData.redirect_type === "smart" ? "bg-slate-900 dark:bg-white" : "bg-slate-200 dark:bg-slate-700"}`}
                >
                  <span className={`inline-block h-5 w-5 bg-white dark:bg-slate-900 rounded-full shadow transform transition-transform mt-0.5 ${formData.redirect_type === "smart" ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
                <span className="text-xs text-slate-500 dark:text-slate-400">Ative para camuflagem inteligente com proteção anti-bot.</span>
              </div>

              {formData.redirect_type === "smart" && (
                <>
                  <div>
                    <label className={labelCls}>Link de afiliado <span className="text-red-500">*</span></label>
                    <input type="text" name="affiliate_link" value={formData.affiliate_link} onChange={handleChange} className={inputCls} placeholder="Cole seu link de afiliado aqui" />
                  </div>
                  <div>
                    <label className={labelCls}>Link do produtor <span className="text-red-500">*</span></label>
                    <input type="text" name="producer_link" value={formData.producer_link} onChange={handleChange} className={inputCls} placeholder="Link do produtor (sem ID de afiliado)" />
                  </div>
                  <div className="flex items-start justify-between gap-4 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
                    <div>
                      <code className="text-sm font-mono font-bold text-slate-900 dark:text-slate-100">data-sobeir</code>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        Adicione <code className="font-mono">data-sobeir</code> em todos os links/botões da sua presell para acionar a proteção smart.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => { navigator.clipboard.writeText("data-sobeir"); toast.success("Parâmetro copiado!"); }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-xs shrink-0"
                    >
                      <Copy className="w-3.5 h-3.5" /> Copiar
                    </button>
                  </div>
                </>
              )}

              <div>
                <label className={labelCls}>Redirecionar após captura?</label>
                <div className="flex items-center gap-4">
                  {["yes", "no"].map((val) => (
                    <label key={val} className="flex items-center gap-2 text-sm cursor-pointer select-none">
                      <input type="radio" name="redirect" value={val} checked={formData.redirect === val} onChange={handleChange} />
                      <span className="text-slate-700 dark:text-slate-300">{val === "yes" ? "Sim" : "Não"}</span>
                    </label>
                  ))}
                </div>
              </div>

              {formData.redirect === "yes" && (
                <>
                  <div>
                    <label className={labelCls}>URL de redirecionamento</label>
                    <input type="text" name="redirect_url" value={formData.redirect_url} onChange={handleChange} className={inputCls} placeholder="https://exemplo.com" />
                  </div>
                  <div>
                    <label className={labelCls}>Quantidade máxima de cliques</label>
                    <input type="number" name="max_clicks" value={formData.max_clicks} onChange={handleChange} className={inputCls} placeholder="Deixe em branco para sem limite" />
                  </div>
                </>
              )}

              <NavButtons
                onBack={prevStep}
                onNext={nextStep}
                nextDisabled={
                  !formData.platform || !formData.project_name || !formData.conversion_name ||
                  (formData.redirect_type === "smart" ? !formData.affiliate_link || !formData.producer_link : false)
                }
              />
            </div>
          )}

          {/* ── Step 3: Conversão automática ── */}
          {step === 3 && (
            <div className="space-y-4">
              <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">Conversão automática</h2>
              <div>
                <label className={labelCls}>Usar conversão automática?</label>
                <div className="flex items-center gap-4">
                  {["yes", "no"].map((val) => (
                    <label key={val} className="flex items-center gap-2 text-sm cursor-pointer select-none">
                      <input type="radio" name="auto_conversion" value={val} checked={formData.auto_conversion === val} onChange={handleChange} />
                      <span className="text-slate-700 dark:text-slate-300">{val === "yes" ? "Sim" : "Não"}</span>
                    </label>
                  ))}
                </div>
              </div>
              <NavButtons onBack={prevStep} onNext={() => formData.auto_conversion === "yes" ? nextStep() : setStep(6)} nextDisabled={!formData.auto_conversion} />
            </div>
          )}

          {/* ── Step 4: Conta Google Ads ── */}
          {step === 4 && formData.auto_conversion === "yes" && (
            <div className="space-y-4">
              <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">Selecione a conta Google Ads</h2>
              <div>
                <label className={labelCls}>Conta Google Ads</label>
                <select name="selected_google_account_id" value={formData.selected_google_account_id} onChange={handleChange} className={inputCls}>
                  <option value="">Selecione...</option>
                  {availableAccounts.map((acc) => (
                    <option key={acc.id || acc.google_id} value={acc.id || acc.google_id}>{acc.email}</option>
                  ))}
                </select>
              </div>
              <NavButtons onBack={prevStep} onNext={nextStep} nextDisabled={!formData.selected_google_account_id} />
            </div>
          )}

          {/* ── Step 5: Pixel ── */}
          {step === 5 && formData.auto_conversion === "yes" && (
            <div className="space-y-4">
              <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">Onde está o pixel?</h2>

              <div className="space-y-2">
                {[{ value: "mcc", label: "MCC" }, { value: "submcc", label: "Sub-MCC" }, { value: "account", label: "Conta" }].map((option) => (
                  <OptionButton
                    key={option.value}
                    selected={formData.selected_pixel_level === option.value}
                    onClick={() => setFormData((prev) => ({ ...prev, selected_pixel_level: option.value, selected_mcc_id: "", selected_submcc_id: "", selected_pixel_id: "" }))}
                  >
                    <span className="text-sm font-medium text-slate-900 dark:text-slate-100">Pixel está no nível de {option.label}</span>
                  </OptionButton>
                ))}
              </div>

              {/* MCC */}
              {formData.selected_pixel_level === "mcc" && (
                <div className="space-y-3 pt-2">
                  <label className={labelCls}>Selecione a MCC</label>
                  {loadingMCCs ? (
                    <div className="text-sm text-slate-500">Carregando MCCs...</div>
                  ) : availableMCCs.length === 0 ? (
                    <div className="text-sm text-slate-500">Nenhuma MCC encontrada</div>
                  ) : (
                    availableMCCs.map((acc, idx) => acc.customer ? (
                      <OptionButton key={acc.customer.id || idx} selected={formData.selected_mcc_id === acc.customer.id}
                        onClick={() => setFormData((prev) => ({ ...prev, selected_mcc_id: acc.customer.id, selected_pixel_id: "" }))}>
                        <span className="text-sm text-slate-900 dark:text-slate-100">{acc.customer.descriptiveName} ({acc.customer.id})</span>
                      </OptionButton>
                    ) : null)
                  )}
                  {formData.selected_mcc_id && (
                    <div className="pt-2">
                      <label className={labelCls}>Selecione o pixel</label>
                      {loadingPixels ? <div className="text-sm text-slate-500">Carregando pixels...</div> : (
                        <select name="selected_pixel_id" value={formData.selected_pixel_id} onChange={handleChange} className={inputCls}>
                          <option value="">Selecione...</option>
                          {availablePixels.map((pixel) => (
                            <option key={pixel.id} value={pixel.id}>{pixel.conversion_name || pixel.name} ({pixel.conversion_action_id || pixel.id})</option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Sub-MCC */}
              {formData.selected_pixel_level === "submcc" && (
                <div className="space-y-3 pt-2">
                  {!formData.selected_mcc_id ? (
                    <>
                      <label className={labelCls}>Selecione a MCC pai</label>
                      {loadingMCCs ? <div className="text-sm text-slate-500">Carregando MCCs...</div> :
                        availableMCCs.length === 0 ? <div className="text-sm text-slate-500">Nenhuma MCC encontrada</div> :
                        availableMCCs.map((acc, idx) => acc.customer ? (
                          <OptionButton key={acc.customer.id || idx} selected={formData.selected_mcc_id === acc.customer.id}
                            onClick={() => setFormData((prev) => ({ ...prev, selected_mcc_id: acc.customer.id, selected_submcc_id: "", selected_pixel_id: "" }))}>
                            <span className="text-sm text-slate-900 dark:text-slate-100">{acc.customer.descriptiveName} ({acc.customer.id})</span>
                          </OptionButton>
                        ) : null)
                      }
                    </>
                  ) : (
                    <>
                      <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 text-sm font-medium text-slate-900 dark:text-slate-100">
                        MCC pai: {availableMCCs.find((a) => a.customer?.id === formData.selected_mcc_id)?.customer?.descriptiveName || ""} ({formData.selected_mcc_id})
                      </div>
                      {loadingSubMCCs ? <div className="text-sm text-slate-500">Carregando Sub-MCCs...</div> :
                        subMCCs.length === 0 ? <div className="text-sm text-slate-500">Nenhuma Sub-MCC encontrada</div> :
                        subMCCs.map((acc, idx) => (
                          <OptionButton key={acc.customerClient.clientCustomer || idx}
                            selected={formData.selected_submcc_id === acc.customerClient.clientCustomer.replace("customers/", "")}
                            onClick={() => setFormData((prev) => ({ ...prev, selected_submcc_id: acc.customerClient.clientCustomer.replace("customers/", ""), selected_pixel_id: "" }))}>
                            <span className="text-sm text-slate-900 dark:text-slate-100">{acc.customerClient.descriptiveName} ({acc.customerClient.clientCustomer.replace("customers/", "")})</span>
                          </OptionButton>
                        ))
                      }
                      {formData.selected_submcc_id && (
                        <div className="pt-2">
                          <label className={labelCls}>Selecione o pixel</label>
                          {loadingPixels ? <div className="text-sm text-slate-500">Carregando pixels...</div> : (
                            <select name="selected_pixel_id" value={formData.selected_pixel_id} onChange={handleChange} className={inputCls}>
                              <option value="">Selecione...</option>
                              {availablePixels.map((pixel) => (
                                <option key={pixel.id} value={pixel.id}>{pixel.conversion_name || pixel.name} ({pixel.conversion_action_id || pixel.id})</option>
                              ))}
                            </select>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Account */}
              {formData.selected_pixel_level === "account" && (
                <div className="space-y-3 pt-2">
                  <label className={labelCls}>Selecione uma MCC ou Conta</label>
                  {loadingAccounts ? <div className="text-sm text-slate-500">Carregando contas...</div> : (
                    <select name="selected_mcc_id" value={formData.selected_mcc_id} onChange={async (e) => {
                      const selectedId = e.target.value;
                      const selected = availableAccountsNivel.find((acc) => acc.customer?.id === selectedId);
                      if (selected?.customer?.manager) {
                        setFormData((prev) => ({ ...prev, selected_mcc_id: selectedId, selected_submcc_id: "", selected_pixel_id: "" }));
                        setAccountsOfMCC([]); setLoadingChildAccounts(true);
                        try {
                          const res = await axios.get(`${PROXY_BASE_URL}/affiliate/google-ads/available-accounts?google_ads_account_id=${formData.selected_google_account_id}&level=account&parent_customer_id=${selectedId}`, { headers: getAuthHeaders() });
                          let list = Array.isArray(res.data) ? res.data : res.data.accounts || [];
                          setAccountsOfMCC(list.filter((acc) => acc.customer?.manager === false && acc.customer?.status === "ENABLED"));
                        } catch { setAccountsOfMCC([]); }
                        setLoadingChildAccounts(false);
                      } else {
                        setAccountsOfMCC([]);
                        setFormData((prev) => ({ ...prev, selected_mcc_id: selectedId, selected_submcc_id: selectedId, selected_pixel_id: "" }));
                      }
                    }} className={inputCls}>
                      <option value="">Selecione uma MCC ou Conta...</option>
                      {availableAccountsNivel.map((acc, idx) => acc.customer ? (
                        <option key={acc.customer.id || idx} value={acc.customer.id}>
                          {acc.customer.manager ? `MCC: ${acc.customer.descriptiveName}` : `Conta: ${acc.customer.descriptiveName}`} ({acc.customer.id})
                        </option>
                      ) : null)}
                    </select>
                  )}
                  {loadingChildAccounts && <div className="text-sm text-slate-500">Carregando contas gerenciadas...</div>}
                  {accountsOfMCC.length > 0 && !loadingChildAccounts && (
                    <div>
                      <label className={labelCls}>Selecione uma conta gerenciada</label>
                      <select name="selected_submcc_id" value={formData.selected_submcc_id}
                        onChange={(e) => setFormData((prev) => ({ ...prev, selected_submcc_id: e.target.value, selected_pixel_id: "" }))} className={inputCls}>
                        <option value="">Selecione a conta...</option>
                        {accountsOfMCC.map((acc, idx) => acc.customer ? (
                          <option key={acc.customer.id || idx} value={acc.customer.id}>Conta: {acc.customer.descriptiveName} ({acc.customer.id})</option>
                        ) : null)}
                      </select>
                    </div>
                  )}
                  {(formData.selected_submcc_id || (formData.selected_mcc_id && availableAccountsNivel.find((a) => a.customer?.id === formData.selected_mcc_id && !a.customer?.manager))) && (
                    <div>
                      <label className={labelCls}>Selecione o pixel</label>
                      {loadingPixels ? <div className="text-sm text-slate-500">Carregando pixels...</div> : (
                        <select name="selected_pixel_id" value={formData.selected_pixel_id}
                          onChange={(e) => setFormData((prev) => ({ ...prev, selected_pixel_id: e.target.value }))} className={inputCls}>
                          <option value="">Selecione...</option>
                          {availablePixels.map((pixel) => (
                            <option key={pixel.id} value={pixel.id}>{pixel.conversion_name || pixel.name} ({pixel.conversion_action_id || pixel.id})</option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                </div>
              )}

              <NavButtons onBack={prevStep} onNext={nextStep} nextDisabled={!formData.selected_pixel_id} />
            </div>
          )}

          {/* ── Step 6: Resumo ── */}
          {step === 6 && (
            <div className="space-y-4">
              <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">Revise antes de gerar</h2>

              <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 divide-y divide-slate-200 dark:divide-slate-700">
                {[
                  { label: "Plataforma", value: formData.platform },
                  { label: "Projeto", value: formData.project_name },
                  { label: "Tipo de funil", value: formData.funnel_mode === "bridge_vsl" ? "Bridge Page + VSL + Produtor" : "Presell + Página do Produtor" },
                  { label: "Ação de conversão", value: formData.conversion_name },
                  { label: "Link de afiliado", value: formData.affiliate_link || "—" },
                  { label: "Link do produtor", value: formData.producer_link || "—" },
                  { label: "Tipo de redirecionamento", value: formData.redirect_type === "smart" ? "Camuflagem Inteligente" : formData.redirect_type === "always_affiliate" ? "Sempre Link de Afiliado" : "Sempre Link do Produtor" },
                  { label: "Redirecionar", value: formData.redirect === "yes" ? `Sim (${formData.redirect_url || "sem URL"})` : "Não" },
                  ...(formData.max_clicks ? [{ label: "Máx. Cliques", value: formData.max_clicks }] : []),
                  { label: "Conversão automática", value: formData.auto_conversion === "yes" ? "Sim" : "Não" },
                  ...(formData.auto_conversion === "yes" ? [
                    { label: "Conta Google Ads", value: availableAccounts.find((a) => (a.id || a.google_id) === formData.selected_google_account_id)?.email || "—" },
                    { label: "Nível do pixel", value: formData.selected_pixel_level || "—" },
                    { label: "MCC", value: availableMCCs.find((m) => m.customer?.id === formData.selected_mcc_id)?.customer?.descriptiveName || "—" },
                    { label: "Pixel", value: availablePixels.find((p) => p.id === formData.selected_pixel_id)?.conversion_name || availablePixels.find((p) => p.id === formData.selected_pixel_id)?.name || "—" },
                  ] : []),
                ].map(({ label, value }) => (
                  <div key={label} className="flex items-start gap-4 px-4 py-3">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400 w-40 shrink-0">{label}</span>
                    <span className="text-sm text-slate-900 dark:text-slate-100 break-all">{value}</span>
                  </div>
                ))}
              </div>

              <NavButtons onBack={prevStep} onNext={handleGenerateScript} nextLabel="Gerar script" loading={generating} />
            </div>
          )}

          {/* ── Step 7: Script gerado ── */}
          {step === 7 && (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">Script gerado com sucesso</h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Copie o script correto para cada página do funil.
                  </p>
                </div>
                <button
                  onClick={() => navigate("/sobescripts")}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100 shrink-0"
                >
                  <Check className="w-4 h-4" /> Finalizar
                </button>
              </div>

              {formData.funnel_mode === "bridge_vsl" ? (
                <div className="grid md:grid-cols-2 gap-4">
                  <ScriptCard title="Bridge Page" subtitle="Primeira página do funil" script={bridgeScriptText} onCopy={() => handleCopyToClipboard(bridgeScriptText)} />
                  <ScriptCard title="VSL" subtitle="Página da carta de vendas" script={vslScriptText} onCopy={() => handleCopyToClipboard(vslScriptText)} />
                </div>
              ) : (
                <ScriptCard title="Script único" subtitle="Use este script na página que deseja rastrear" script={fullScriptText} onCopy={() => handleCopyToClipboard(fullScriptText)} />
              )}

              <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
                <p className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Como usar:</p>
                {formData.funnel_mode === "bridge_vsl" ? (
                  <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1 list-disc list-inside">
                    <li>Cole o script da <strong>Bridge Page</strong> na primeira página do funil.</li>
                    <li>Cole o script da <strong>VSL</strong> na página da carta de vendas.</li>
                    <li>Mantenha os dois scripts no mesmo domínio/projeto da campanha.</li>
                  </ul>
                ) : (
                  <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1 list-disc list-inside">
                    <li>Cole este script dentro da tag &lt;head&gt; ou antes de &lt;/body&gt;.</li>
                    <li>Use em todas as páginas que deseja rastrear.</li>
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SobeScript;